import {
  CurrentActionDto,
  KnownCurrentActionDtoType,
  KnownOrderDtoStatus,
  KnownOrderStatus,
  KnownTimelineEventDtoNotificationChannel,
  KnownTimelineEventDtoNotificationStatus,
  KnownTimelineEventDtoStatus,
  KnownTimelineEventDtoType,
  OrderDto,
  TimelineEventDto,
  KnownHubDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TimelineEventProps } from '../../../../../ui/components/pure/timeline/timeline.types';
import { Colors } from '../../../../../ui/theme/colors';
import { orderStatusBackgroundColors } from '../../../orders.constants';
import { OrderDetailsActions } from '../../actions/order-details.actions';
import { eventIcons } from './order-timeline.constants';
import {
  mapDeliveryNotificationToColor,
  mapDeliveryNotificationToIcon
} from '../../../../deliveries/deliveries.constants';
import { transformTimelineTextForProject } from '../../../../../core/util/status-label.util';
import { BookingFlowType } from '../../../../../ui/enums/booking-flow-type.enum';

function makeTimestamp(timestamp: Date) {
  return `${timestamp.toLocaleDateString('en-AU')} @ ${timestamp.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit'
  })}`;
}

function makeMessage(timestamp: Date, description?: string, bookingFlowType?: BookingFlowType) {
  const transformedDescription =
    bookingFlowType && description ? transformTimelineTextForProject(description, bookingFlowType) : description;
  return `${makeTimestamp(timestamp)}${transformedDescription ? ` | ${transformedDescription}` : ''}`;
}

function makeDetails(
  details: TimelineEventDto['details'],
  bookingFlowType?: BookingFlowType
): TimelineEventProps['details'] {
  return details?.title ?? details?.content
    ? {
        title: bookingFlowType
          ? transformTimelineTextForProject(details?.title, bookingFlowType) || details?.title || ''
          : details?.title ?? '',
        content: bookingFlowType
          ? transformTimelineTextForProject(details?.content, bookingFlowType) || details?.content || ''
          : details?.content ?? '',
        imageURL: details?.imageURL ?? '',
        disclaimer: bookingFlowType
          ? transformTimelineTextForProject(details?.disclaimer, bookingFlowType) || details?.disclaimer
          : details?.disclaimer
      }
    : undefined;
}

function makeTitle(event: TimelineEventDto, bookingFlowType?: BookingFlowType): string {
  // If the event is a note, and the title is internal, then we want to show the internal badge
  switch (event.type) {
    case KnownTimelineEventDtoType.NoteCreated:
      return 'Note';
    default:
      return bookingFlowType
        ? transformTimelineTextForProject(event.title, bookingFlowType) || event.title
        : event.title;
  }
}

function makeTitleBadge(event: TimelineEventDto): TimelineEventProps['titleBadge'] | undefined {
  // If the event is a note, and the title is internal, then we want to show the internal badge
  return event.type === KnownTimelineEventDtoType.NoteCreated && event.title.includes('Internal')
    ? {
        label: 'Internal',
        bgColor: Colors.informativeYellow,
        textColor: Colors.burntOrange,
        tooltip: {
          label: makeMessage(event.timestamp, event.description)
        }
      }
    : undefined;
}

function makeMessageBadge(event: TimelineEventDto): TimelineEventProps['messageBadge'] | undefined {
  // Create a badge based on the notification status
  return event.notificationStatus
    ? {
        label: event.notificationStatus,
        bgColor: mapDeliveryNotificationToColor[event.notificationStatus as KnownTimelineEventDtoNotificationStatus],
        tooltip: {
          label: event.notificationDetails
            ? makeMessage(event.notificationDetails.timestamp, event.notificationDetails.message)
            : event.notificationStatus
        }
      }
    : undefined;
}

const getTimelineEventIcon = (
  type: KnownCurrentActionDtoType | KnownTimelineEventDtoType,
  notificationChannel?: KnownTimelineEventDtoNotificationChannel
) => {
  if (notificationChannel) {
    return mapDeliveryNotificationToIcon[notificationChannel];
  }
  return eventIcons[type];
};

export function makeTimelineEvent(
  event?: TimelineEventDto,
  bookingFlowType?: BookingFlowType
): TimelineEventProps | undefined {
  return event
    ? {
        icon: {
          color:
            orderStatusBackgroundColors[
              (event.delivery?.status ?? event.status ?? 'in-progress') as KnownTimelineEventDtoStatus
            ],
          Icon: getTimelineEventIcon(
            event.type as KnownTimelineEventDtoType,
            event.notificationChannel as KnownTimelineEventDtoNotificationChannel
          )
        },
        title: makeTitle(event, bookingFlowType),
        titleBadge: makeTitleBadge(event),
        message: makeMessage(event.timestamp, event.description, bookingFlowType),
        details: makeDetails(event.details, bookingFlowType),
        subtext: bookingFlowType
          ? transformTimelineTextForProject(event.subtext, bookingFlowType) || event.subtext
          : event.subtext,
        messageBadge: makeMessageBadge(event)
      }
    : undefined;
}

export function makeCurrentAction(
  currentAction: CurrentActionDto | undefined,
  setAction: (action: OrderDetailsActions) => void,
  order?: OrderDto,
  bookingFlowType?: BookingFlowType
): TimelineEventProps | undefined {
  if (!currentAction) return undefined;
  if (!order) return undefined;

  // For inventory flow projects, never show current action when order is Booked
  if (order.status === KnownOrderStatus.Booked && bookingFlowType === BookingFlowType.INVENTORY) {
    return undefined;
  }

  // There are no actions to take if the order is not booked, delivered or retrieval is not required
  if (
    order.status !== KnownOrderStatus.Booked &&
    order.status !== KnownOrderDtoStatus.Delivered &&
    order.status !== KnownOrderDtoStatus.RetrievalRequired
  ) {
    return undefined;
  }

  const isExternalDelivery = order.hub?.type === KnownHubDtoType.LockersHubExternal;

  return currentAction && order
    ? {
        icon: {
          color: orderStatusBackgroundColors[order.status as KnownOrderDtoStatus],
          Icon: getTimelineEventIcon(currentAction.type as KnownTimelineEventDtoType)
        },
        title: bookingFlowType
          ? transformTimelineTextForProject(currentAction.title, bookingFlowType) || currentAction.title
          : currentAction.title,
        ...(order.status === KnownOrderStatus.Booked
          ? {
              action: {
                buttonLabel: bookingFlowType
                  ? transformTimelineTextForProject('Deliver Now', bookingFlowType) || 'Deliver Now'
                  : 'Deliver Now',
                buttonColor: Colors.deliveredBlue,
                onClick: () => setAction(OrderDetailsActions.PORTAL_HANDOFF)
              }
            }
          : order.status === KnownOrderDtoStatus.Delivered
          ? {
              action: {
                buttonLabel: 'Mark Collected',
                buttonColor: Colors.collectedGreen,
                onClick: () => setAction(OrderDetailsActions.MARK_COLLECTED)
              }
            }
          : order.status === KnownOrderDtoStatus.RetrievalRequired
          ? {
              action: {
                buttonLabel: 'Retrieve Now',
                buttonColor: Colors.retrievalRequiredPurple,
                onClick: () => setAction(OrderDetailsActions.PORTAL_HANDOFF)
              }
            }
          : {
              qr: {
                title: bookingFlowType
                  ? transformTimelineTextForProject(currentAction.details?.heading, bookingFlowType) ||
                    currentAction.details?.heading
                  : currentAction.details?.heading,
                message: bookingFlowType
                  ? transformTimelineTextForProject(currentAction.details?.disclaimer, bookingFlowType) ||
                    currentAction.details?.disclaimer
                  : currentAction.details?.disclaimer,
                qrCode:
                  (isExternalDelivery ? currentAction.details?.barcodeContent : currentAction.details?.qrcodeContent) ||
                  ''
              }
            }),
        options: {
          dividerStyle: 'dashed',
          alignTitle: 'middle'
        }
      }
    : undefined;
}
